import React from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowChevronArrow from './components/WorkflowChevronArrow';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLinkCard from './components/WorkflowLinkCard';
import WorkflowList from './components/WorkflowList';
import WorkflowLockedCodeBlock from './components/WorkflowLockedCodeBlock';
import WorkflowProcedureFlow from './components/WorkflowProcedureFlow';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const seriesSteps = [
  {
    label: 'プロジェクト作成',
    title: 'プロジェクト作成 + Steeringルール設置',
    path: '/devdocs/handson/project-setup',
  },
  {
    label: 'API仕様書取込',
    title: 'API仕様書の取り込み',
    path: '/devdocs/handson/api-specs',
  },
  { label: '画面入出力定義書', title: '画面入出力定義書の作成', active: true },
  { label: '画面実装', title: '画面実装', path: '/devdocs/handson/implementation' },
];

const HandsonScreenIo: React.FC = () => {
  return (
    <HandsonArticleLayout
      title="フロントエンド編 — 画面入出力定義書の作成"
      seriesName="フロントエンド編"
      tags={['Hands-on', 'Frontend', 'Screen IO', 'PDCA']}
      seriesSteps={seriesSteps}
      prevLink={{
        label: 'API仕様書の取り込み',
        path: '/devdocs/handson/api-specs',
      }}
      nextLink={{
        label: '画面実装',
        path: '/devdocs/handson/implementation',
      }}
    >
      <WorkflowGoalBox
        prerequisites={['「フロントエンド編 — API仕様書の取り込み」が完了している']}
        completionCriteria={[
          '.kiro/steering/screen_io_rule.md が作成されている',
          'docs/screens/ 配下に画面入出力定義書が生成されている',
          'セクション4のチェックリスト全項目に合格している',
          '少なくとも1回は改善→再生成のサイクルを経験している',
        ]}
      >
        画面入出力定義書の生成ルールを設置し、AIで定義書を生成する。チェックリストで品質を確認し、改善サイクルを回して画面実装の入力を完成させる。
      </WorkflowGoalBox>

      <WorkflowLinkCard
        title="フロントエンド編 — API仕様書の取り込み"
        description="前の記事"
        path="/devdocs/handson/api-specs"
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'この章で体験すること' },
          { id: 'section-2', label: 'Steeringルールを設置する' },
          { id: 'section-3', label: 'AIで画面入出力定義書を生成する' },
          { id: 'section-4', label: 'チェックリストで品質を確認する' },
          { id: 'section-5', label: '改善して再生成する' },
          { id: 'section-6', label: '現在のプロジェクト構成' },
        ]}
      />

      {/* セクション1: この章で体験すること */}
      <WorkflowSection title="この章で体験すること" id="section-1" number={1}>
        <WorkflowSection title="画面入出力定義書とは" subNumber="1.1">
          <WorkflowText>
            画面入出力定義書は「各画面で何を表示し、何を入力させ、どのAPIと連携するか」を定義するドキュメントです。次の記事（画面実装）でAIに画面を作らせるときの入力として使います。
          </WorkflowText>
          <WorkflowText>
            API仕様書が「バックエンドとの通信仕様」を定義するのに対し、画面入出力定義書は「ユーザーが実際に触る画面の仕様」を定義します。
          </WorkflowText>

          <WorkflowProcedureFlow
            steps={[
              {
                label: '入力',
                sub: 'api_specs.yaml / proposal.md / business.md',
                variant: 'external',
              },
              { label: '出力', sub: '画面入出力定義書（1画面1ファイル docs/screens/*.md）' },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="PDCAサイクル — 再び" subNumber="1.2">
          <WorkflowText>
            要件定義編で体験した「ルール→生成→チェック→改善」のサイクルを、画面入出力定義書でも回します。
          </WorkflowText>

          <Grid container spacing={1} sx={{ maxWidth: 480, mx: 'auto', my: 2 }}>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Plan"
                sub="ルール設置"
                bgcolor="#1565c0"
                borderColor="#0d47a1"
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid size={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowForwardIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            </Grid>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Do"
                sub="AI生成"
                bgcolor="#2e7d32"
                borderColor="#1b5e20"
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid size={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowUpwardIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            </Grid>
            <Grid size={2} />
            <Grid size={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowDownwardIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            </Grid>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Act"
                sub="修正指示"
                bgcolor="#e65100"
                borderColor="#bf360c"
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid size={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowForwardIcon
                sx={{ fontSize: 22, color: 'text.disabled', transform: 'rotate(180deg)' }}
              />
            </Grid>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Check"
                sub="過不足確認"
                bgcolor="#f57f17"
                borderColor="#e65100"
                sx={{ height: '100%' }}
              />
            </Grid>
          </Grid>

          <WorkflowText>
            今回のCheckは「生成された画面項目が過不足ないか」を受講者が判断するフェーズです。APIのフィールドが画面に全て反映されているか、逆に不要な項目が含まれていないかを確認します。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="この章の流れ" subNumber="1.3">
          <WorkflowList
            variant="ordered"
            items={[
              'Steeringルールを設置する（screen_io_rule.md）',
              'AIで画面入出力定義書を生成する',
              'チェックリストで品質を確認する',
              '不合格箇所を改善して再生成する',
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション2: Steeringルールを設置する */}
      <WorkflowSection title="Steeringルールを設置する" id="section-2" number={2}>
        <WorkflowSection title="何を書くか" subNumber="2.1">
          <WorkflowText>screen_io_rule.mdには以下を定義します:</WorkflowText>
          <WorkflowList
            items={[
              '画面入出力定義書のフォーマット（セクション構成）',
              '入力として参照するファイル',
              '配置先とファイル命名規則',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="完成例をコピペする" subNumber="2.2">
          <WorkflowText>
            画面入出力定義書のフォーマット設計は、画面開発の経験がないと「何をどの粒度で書くか」の判断が難しい部分です。今回は完成例をそのまま使ってください。
          </WorkflowText>
          <WorkflowText>
            React経験者でUI/UXを自分で設計したい方は、この完成例を参考にしつつ自分でフォーマットを考えて書いても構いません。その場合、画面実装時にAIが正しく読み取れるよう「画面項目」「API連携」「エラーハンドリング」の3セクションは最低限含めることを推奨します。
          </WorkflowText>
          <WorkflowText>
            以下の完成例をコピーして .kiro/steering/screen_io_rule.md として保存してください。
          </WorkflowText>

          <WorkflowLockedCodeBlock password="Wn4xLq" showPassword>
            {`---
inclusion: manual
---

# 画面入出力定義書 生成ルール

## 目的

api_specs.yaml と、プロンプトで指定されるバックエンドの rfp.md を起点に関連ファイルを参照し、
各画面の入出力定義書を生成する。

## 入力ファイル

### フロントプロジェクト内

| ファイル | 参照する情報 |
|----------|-------------|
| \`docs/api_specs.yaml\` | APIのRequest/Response/エンドポイント |
| \`.kiro/steering/product.md\` | 画面一覧・ユーザーストーリー（always読み込み） |

### バックエンドプロジェクト

プロンプトで指定された rfp.md のパスを起点に、同リポジトリ内の関連ファイルを探索して参照する。

| ファイル | 参照する情報 |
|----------|-------------|
| rfp.md（プロンプトで指定） | 機能要件・画面の概要 |
| proposal.md（rfpと同じ \`.kiro/requirements/\` 配下） | 画面とAPIの対応関係 |
| business.md（\`.kiro/requirements/usecases/\` 配下） | 業務フロー・画面遷移 |
| usecases.md（\`.kiro/requirements/usecases/\` 配下） | ユースケース・入出力仕様 |

バックエンドプロジェクトの \`.kiro/steering/structure.md\` を読み込み、ファイル配置を把握した上で探索すること。

## 出力先

- \`docs/screens/\` 配下に1画面1ファイルで出力する
- ファイル名: \`{画面ID}_{画面名}.md\`
- 例: \`SCR001_TODO一覧.md\`, \`SCR002_TODO登録.md\`

## 画面IDの採番

- \`SCR\` + 3桁ゼロ埋め連番（SCR001, SCR002, ...）
- proposal.md の画面一覧に記載された順番で採番する

## ドキュメント構造

各画面の定義書は以下のセクションで構成する。

### テンプレート

\`\`\`
# {画面ID} {画面名}

## 画面概要

| 項目 | 内容 |
|------|------|
| 画面ID | {画面ID} |
| 画面名 | {画面名} |
| パス | {URLパス} |
| 概要 | {画面の目的を1〜2文で} |

## 画面項目

| # | 項目名 | 種別 | 必須 | 桁数 | 入力形式 | 備考 |
|---|--------|------|------|------|----------|------|
| 1 | ... | ... | ... | ... | ... | ... |

## アクション

| # | アクション名 | トリガー | API | 遷移先 | 処理内容 |
|---|-------------|---------|-----|--------|----------|
| 1 | ... | ... | ... | ... | ... |

## API連携

### {API名}（{Method} {Endpoint}）

#### リクエストマッピング

| 画面項目 | APIフィールド | 変換 |
|----------|-------------|------|
| ... | ... | ... |

#### レスポンスマッピング

| APIフィールド | 画面項目 | 変換 |
|-------------|----------|------|
| ... | ... | ... |

## エラーハンドリング

| HTTPステータス | 表示方法 | メッセージ |
|--------------|----------|-----------|
| 400 | ... | ... |
| 404 | ... | ... |
| 500 | ... | ... |
\`\`\`

## セクション別 記載ルール

### 画面項目

- 種別: \`表示\` / \`入力（テキスト）\` / \`入力（セレクト）\` / \`入力（日付）\` / \`入力（チェックボックス）\` / \`ボタン\` / \`リンク\`
- 必須: 入力項目の場合のみ \`〇\` を記載。表示項目・ボタンは空欄
- 桁数: 入力項目の場合、最大文字数を記載。api_specs.yaml の maxLength と一致させる
- 入力形式: 自由入力 / プルダウン / 日付ピッカー / チェックボックス等
- 表示のみの項目（APIレスポンスから表示する項目）も漏れなく記載する
- 一覧画面の場合、テーブルの各列を個別の項目として記載する

### アクション

- トリガー: ボタンクリック / フォーム送信 / 画面遷移時 / リンククリック
- API: 呼び出すAPIのメソッド+エンドポイント。API呼び出しがない場合は「なし」
- 遷移先: 画面遷移がある場合は遷移先の画面ID+画面名。遷移なしの場合は「なし（画面更新）」
- 処理内容: UIの振る舞い（ダイアログ表示、一覧リフレッシュ等）を1行で

### API連携

- 画面項目とAPIフィールドの対応を双方向で記載する
- 変換: そのまま / 日付フォーマット変換 / ID→表示名変換 等。変換なしの場合は「そのまま」
- 1つの画面が複数APIを呼ぶ場合は、APIごとにサブセクションを分ける

### エラーハンドリング

- api_specs.yaml に定義されたエラーレスポンスに対して、画面での表示方法を定義する
- 表示方法: トースト通知 / インラインエラー / ダイアログ / 画面遷移
- メッセージ: ユーザーに表示する日本語メッセージ

## 禁止事項

- api_specs.yaml に存在しないAPIを参照しない
- proposal.md に記載のない画面を追加しない
- 画面項目にAPIフィールドと対応しない項目を独自に追加しない（UIの操作ボタンは除く）

## チェックリスト

生成後に以下を確認する:

- [ ] proposal.md の画面一覧と画面IDが1対1で対応しているか
- [ ] 各画面の画面項目がAPIのRequest/Responseフィールドを網羅しているか
- [ ] 入力項目の桁数がapi_specs.yamlのmaxLengthと一致しているか
- [ ] アクションのAPI列がapi_specs.yamlに存在するエンドポイントか
- [ ] エラーハンドリングにapi_specs.yamlの全エラーレスポンスが含まれているか
- [ ] 一覧画面のレスポンスマッピングが配列（results）を前提としているか`}
          </WorkflowLockedCodeBlock>

          <WorkflowTipBox title="MUIを使わず自分でデザインする場合">
            <WorkflowText>
              React経験者でMUIを使わず独自デザインで実装する場合は、画面入出力定義書に以下の情報を追記することを推奨します:
            </WorkflowText>
            <WorkflowList
              items={[
                'レイアウト構成（ヘッダー・サイドバー・メインコンテンツの配置）',
                '配色・フォントの指定',
                '使用するCSSフレームワーク（Tailwind CSS等）の指定',
                'コンポーネントの見た目の詳細（角丸、シャドウ、余白等）',
              ]}
            />
            <WorkflowText>
              これらを定義書に書いておくことで、次の記事（画面実装）でAIがデザイン意図を正確に汲み取れます。MUIを使う場合はMUIのデフォルトデザインが適用されるため、これらの追記は不要です。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション3: AIで画面入出力定義書を生成する */}
      <WorkflowSection title="AIで画面入出力定義書を生成する" id="section-3" number={3}>
        <WorkflowSection title="出力先ディレクトリを作成する" subNumber="3.1">
          <WorkflowCodeBlock language="bash" code={`mkdir -p docs/screens`} />
        </WorkflowSection>

        <WorkflowSection title="生成プロンプト" subNumber="3.2">
          <WorkflowText>Kiroのチャットに以下のプロンプトを入力します。</WorkflowText>

          <WorkflowChatBubble role="human">
            {`#screen_io_rule バックエンドプロジェクトの rfp.md を起点に関連ファイルを参照して、
全画面の画面入出力定義書を docs/screens/ に生成してください。
rfp.md のパス: {あなたのバックエンドプロジェクトの絶対パス}/.kiro/requirements/todo/todo.rfp.md`}
          </WorkflowChatBubble>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              product.mdは always
              モード（常時読み込み）なのでプロンプトで指定不要です。rfp.mdの絶対パスを渡すことで、AIがバックエンドリポジトリ内のstructure.mdを読み取り、proposal.md
              / business.md / usecases.mdの配置先を自分で探索して参照します。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="生成結果" subNumber="3.3">
          <WorkflowText>
            AIが docs/screens/
            配下に画面ごとのMarkdownファイルを生成します。proposal.mdに記載された画面の数だけファイルが作成されます。
          </WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4: チェックリストで品質を確認する */}
      <WorkflowSection title="チェックリストで品質を確認する" id="section-4" number={4}>
        <WorkflowSection title="画面入出力定義書チェックリスト" subNumber="4.1">
          <WorkflowChecklist
            groups={[
              {
                title: '品質チェック',
                items: [
                  'proposal.md の画面一覧に含まれる全画面が作成されているか',
                  '各画面の画面項目がAPIのRequest/Responseフィールドを網羅しているか（過不足がないか）',
                  '入力項目に必須・桁数が記載されているか',
                  'アクションのAPI列がapi_specs.yamlに存在するエンドポイントと一致しているか',
                  'API連携のマッピングが双方向（リクエスト/レスポンス）で記載されているか',
                  'エラーハンドリングにバリデーションエラー（400）とサーバーエラー（500）が含まれているか',
                  '一覧画面の場合、テーブルの各列が個別の画面項目として記載されているか',
                ],
              },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="チェックのポイント — 画面項目の過不足を見極める" subNumber="4.2">
          <WorkflowText>
            このチェックで最も重要なのは「画面項目の過不足」です。これはルールやフォーマットの問題ではなく、あなたの画面設計の判断が問われる部分です。
          </WorkflowText>

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>: AIは api_specs.yaml
              のフィールドを機械的に画面項目に変換します。しかし「このフィールドはユーザーに見せるべきか」「この入力項目は本当に必要か」はあなたが判断する必要があります。
            </Typography>
          </WorkflowInfoBox>

          <WorkflowText>例えば:</WorkflowText>
          <WorkflowList
            items={[
              'APIレスポンスに created_at（作成日時）があるが、一覧画面に表示する必要があるか？',
              'APIリクエストに priority（優先度）があるが、登録画面で入力させるか、デフォルト値にするか？',
            ]}
          />
          <WorkflowText>
            これらはrfp.mdやbusiness.mdの業務フローを踏まえて判断してください。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="不合格パターンと改善ケーススタディ" subNumber="4.3">
          <WorkflowTable
            headers={['#', '症状', '原因', '改善']}
            rows={[
              [
                '1',
                'APIのフィールドが画面項目に反映されていない',
                'AIがレスポンスの一部フィールドを省略した',
                '修正指示で該当フィールドを画面項目に追加させる',
              ],
              [
                '2',
                '画面に不要な内部IDが表示項目として含まれている',
                'AIがAPIフィールドを全て画面項目に変換した',
                '修正指示で内部ID（UUID等）を画面項目から削除させる',
              ],
              [
                '3',
                'エラーハンドリングに404が含まれていない',
                '削除・更新APIの画面で404ケースが漏れている',
                '修正指示で404のハンドリングを追加させる',
              ],
              [
                '4',
                'アクションの遷移先が「なし」だが実際は遷移がある',
                'business.mdの業務フロー（画面遷移）をAIが読み取れていない',
                '修正指示で遷移先を具体的に指定する',
              ],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5: 改善して再生成する */}
      <WorkflowSection title="改善して再生成する" id="section-5" number={5}>
        <WorkflowSection title="修正指示の出し方" subNumber="5.1">
          <WorkflowText>
            チェックで問題が見つかった場合、AIに修正指示を出します。全画面を再生成する必要はなく、問題のある画面だけ修正させます。
          </WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            特定の画面を修正する場合:
          </Typography>
          <WorkflowChatBubble role="human">
            {`#screen_io_rule docs/screens/SCR001_TODO一覧.md を修正してください。

修正内容:
- 画面項目から「TODO ID」（内部UUID）を削除する。ユーザーに表示する必要はない
- 画面項目に「作成日」を追加する。APIレスポンスの created_at を yyyy/MM/dd 形式で表示する
- アクションの「削除」の遷移先を「なし（画面更新）」から「なし（一覧をリフレッシュ）」に修正する`}
          </WorkflowChatBubble>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            全画面のエラーハンドリングを一括修正する場合:
          </Typography>
          <WorkflowChatBubble role="human">
            {`#screen_io_rule docs/screens/ 配下の全画面のエラーハンドリングを確認してください。

修正内容:
- 更新・削除APIを呼ぶ画面に 404（対象なし）のハンドリングが漏れているので追加する
- 表示方法は「ダイアログ」、メッセージは「対象のデータが見つかりません。一覧に戻ります。」`}
          </WorkflowChatBubble>
        </WorkflowSection>

        <WorkflowSection title="改善のコツ" subNumber="5.2">
          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              修正指示は「何を」「どう直すか」を具体的に書いてください。「画面項目を見直して」のような曖昧な指示では、AIが的外れな修正をすることがあります。
            </Typography>
          </WorkflowInfoBox>

          <WorkflowText>
            改善→再チェックを繰り返し、チェックリストの全項目に合格するまでサイクルを回してください。2〜3回のサイクルで品質が安定するはずです。
          </WorkflowText>

          <WorkflowTipBox title="ルールを修正すべきか、個別に修正指示を出すべきか">
            <WorkflowText>判断基準はシンプルです:</WorkflowText>
            <WorkflowList
              items={[
                '同じ問題が複数の画面で繰り返し起きる → screen_io_rule.md を修正する（例: 「内部IDは画面項目に含めない」を禁止事項に追加）',
                '特定の画面だけの問題 → 個別に修正指示を出す（例: 「TODO一覧の画面項目に作成日を追加」）',
              ]}
            />
            <WorkflowText>
              要件定義編の「要件定義編 — proposal/business生成」で学んだのと同じ判断です。
            </WorkflowText>
            <WorkflowLinkCard
              title="要件定義編 — proposal/business生成"
              description="ルール改善の判断基準"
              path="/devdocs/handson/proposal-business"
            />
          </WorkflowTipBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6: 現在のプロジェクト構成 */}
      <WorkflowSection title="現在のプロジェクト構成" id="section-6" number={6}>
        <WorkflowInfoBox type="info">
          <Typography variant="body2" color="text.secondary">
            docs/screens/
            配下のファイル数はあなたのrfpの内容によって異なります。一覧・登録・編集で画面を分けていれば3ファイル以上になりますし、1画面で全操作を行う設計であれば1ファイルになります。以下はあくまで一例です。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowDirectoryTree
          root="team_todo_front/"
          entries={[
            {
              name: '.kiro',
              color: '#1565c0',
              children: [
                {
                  name: 'steering',
                  children: [
                    { name: 'product.md' },
                    { name: 'tech.md' },
                    { name: 'structure.md' },
                    { name: 'api_specs_rule.md' },
                    { name: 'screen_io_rule.md', desc: '今回作成', highlight: '#e65100' },
                  ],
                },
              ],
            },
            {
              name: 'docs',
              color: '#388e3c',
              children: [
                { name: 'api_specs.yaml' },
                {
                  name: 'screens',
                  desc: '今回生成（ファイル数はrfp次第）',
                  highlight: '#1976d2',
                  children: [{ name: 'SCR001_TODO一覧.md' }, { name: 'SCR002_TODO登録.md' }],
                },
              ],
            },
            {
              name: 'node_modules',
              defaultOpen: false,
            },
            {
              name: 'public',
              defaultOpen: false,
            },
            {
              name: 'src',
              children: [{ name: 'App.css' }, { name: 'App.tsx' }, { name: 'main.tsx' }],
            },
            { name: 'HELPME.md' },
            { name: 'index.html' },
            { name: 'package.json' },
            { name: 'tsconfig.json' },
            { name: 'vite.config.ts' },
          ]}
        />
      </WorkflowSection>

      {/* 予告ボックス */}
      <WorkflowInfoBox type="preview">
        <Typography variant="body2" fontWeight={600}>
          次の記事: フロントエンド編 — 画面実装
        </Typography>
        <WorkflowList
          items={[
            'design_guideline.mdを作成してデザインの方向性を定義する',
            '画面入出力定義書をもとにAIに画面を実装させる',
            'バックエンドAPIと接続して動作確認する',
          ]}
        />
      </WorkflowInfoBox>

      {/* チェックリスト */}
      <WorkflowChecklist
        groups={[
          {
            title: 'チェックリスト',
            items: [
              '.kiro/steering/screen_io_rule.md を作成した',
              'docs/screens/ 配下に画面入出力定義書が生成されている',
              'proposal.mdの全画面に対応する定義書が存在する',
              '各画面の画面項目がAPIフィールドと過不足なく対応している',
              'チェックリスト（セクション4）の全項目に合格している',
              '少なくとも1回は修正指示→再確認のサイクルを経験した',
            ],
          },
        ]}
      />
    </HandsonArticleLayout>
  );
};

export default HandsonScreenIo;
